'use client'

import { useEffect, useState } from 'react'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'
import { parseHpEditInput } from '@/lib/hpEdit'
import type { LightSource } from '@/types'
import type { VTTCharacterData } from '@/services/characterService'
import { getCharacterSheetUrl } from '@/services/characterService'
import { Input } from '@arcanecircle/ui/ui/input'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { getTokenCombatState } from '@/utils/tokenCombatState'

/**
 * Canvas overlay chrome — glassy HUD cards floated over the battle
 * map. Two slots wired here:
 *
 *   TR — active map title pill (compass + name + dimensions)
 *   BL — selected-token HUD (avatar + identity + HP bar if character
 *        data is linked + stat grid)
 *
 * The TL tool cluster is owned by `<Toolbar />` rendered inside
 * VTTCanvas (where it can read ruler state directly).
 *
 * Grid layout makes the overlay non-interactive on its own; each slot
 * turns pointer events back on.
 */

interface CanvasOverlayProps {
  /** Optional character-data resolver — if provided, the selected
   *  token's character HP bar / AC / Init render in the HUD card. */
  getCharacter?: (characterId: string) => VTTCharacterData | null
  /** Viewer role. 'gm' unlocks delete + HP-bar visibility toggle and
   *  rename of any token. */
  userRole?: string
  /** Viewer's user id — used to gate rename to the token's owner. */
  currentUserId?: string
  /** Rename the token. Shown to the GM or the token's owner. */
  onRename?: (tokenId: string, newName: string) => void
  /** Delete the token. GM-only (server enforces); UI gated to match. */
  onDelete?: (tokenId: string) => void
  /** GM-only: flip whether players can see this token's HP bar. */
  onHpBarVisibilityChange?: (tokenId: string, visibleToPlayers: boolean) => void
  /** Owner-or-GM: edit the linked character's current HP (issue #135). `set`
   *  is absolute, `delta` is relative ±. Writes the sheet + syncs all clients. */
  onHpEdit?: (
    characterId: string,
    change: { set: number } | { delta: number },
    tokenId?: string,
  ) => void
  /** Owner-or-GM: toggle this token's torch and set its light radius (in grid
   *  squares). Drives token.torchActive / token.lightRadius via token_update. */
  onLightChange?: (
    tokenId: string,
    updates: { lightRadius?: number | null; torchActive?: boolean },
  ) => void
  /** Render the bottom-left selected-object panel. Off on mobile, where the
   *  compact `PropertiesHUD` owns selection instead. The top-right map title
   *  always renders regardless. Defaults to true. */
  showSelectedPanel?: boolean
}

const LIGHT_MIN_RADIUS = 0
const LIGHT_MAX_RADIUS = 60

export function CanvasOverlay({
  getCharacter,
  userRole,
  currentUserId,
  onRename,
  onDelete,
  onHpBarVisibilityChange,
  onHpEdit,
  onLightChange,
  showSelectedPanel = true,
}: CanvasOverlayProps) {
  const {
    getActiveMap,
    getActiveMapTokens,
    selectedTokenIds,
    openCharacterSheet,
    previewingVisionFor,
    setPreviewingVisionFor,
    selectedLightId,
    lightSources,
    updateLightSourceInStore,
    removeLightSource,
    setSelectedLightId,
    ownHpBarHidden,
    toggleOwnHpBarHidden,
  } = useVTTStore()
  const map = getActiveMap()
  const tokens = getActiveMapTokens()
  // HUD card shows one token's identity / HP / stats — only meaningful
  // when exactly one token is selected. With multi-select active we
  // hide the card rather than picking an arbitrary "primary" token.
  const selectedToken =
    selectedTokenIds.length === 1
      ? tokens.find((t) => t.id === selectedTokenIds[0]) ?? null
      : null

  const characterId = selectedToken?.characterId
  const characterData =
    characterId && getCharacter ? getCharacter(characterId) : null

  // Mob tokens track HP per-token (instanceStats); linked tokens read sheet.
  const tokenCombatState = selectedToken
    ? getTokenCombatState(selectedToken, characterData)
    : null
  const displayHp = tokenCombatState?.hp ?? characterData?.stats?.hp ?? { current: 0, max: 0, temp: 0 }

  // Permission gating. Delete is destructive and GM-only (the server
  // enforces this too); rename and the HP-bar visibility toggle are allowed
  // for the GM or the token's owner (canEdit; the server enforces this too).
  const isGM = userRole === 'gm'
  const canEdit =
    !!selectedToken &&
    (isGM ||
      (selectedToken.ownerUserId != null &&
        selectedToken.ownerUserId === currentUserId))
  const canDelete = isGM

  // Inline rename (mirrors TokenTooltip's pattern): an edit affordance
  // swaps the name into an editable input; Enter/blur submits, Esc cancels.
  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState(selectedToken?.name ?? '')
  // Inline HP edit (issue #135). Roll20/Foundry-style: a raw number sets,
  // a +N / -N adjusts. Enter or blur submits, Esc cancels.
  const [hpEditing, setHpEditing] = useState(false)
  const [hpInput, setHpInput] = useState('')
  const submitHp = () => {
    const change = parseHpEditInput(hpInput)
    setHpEditing(false)
    if (!change || !characterId || !onHpEdit) return
    onHpEdit(characterId, change, selectedToken?.id)
  }
  // Two-step delete: first click arms the button, second confirms.
  // Auto-disarms after 4s.
  const [deleteArmed, setDeleteArmed] = useState(false)

  // Selected standalone light (GM-only). The desktop card renders light
  // controls in the same bottom-left chrome as tokens — clicking a light shows
  // its radius + delete here, just as the token card shows token controls.
  const selectedLight = lightSources.find((ls) => ls.id === selectedLightId)

  // Optimistic update + socket emit for any subset of a light's fields.
  const updateLight = (updates: Partial<LightSource>) => {
    if (!selectedLight) return
    updateLightSourceInStore(selectedLight.id, updates)
    try {
      socketService.updateLightSource(selectedLight.id, updates)
    } catch {
      // socket down — optimistic value stands; session replay reconciles
    }
  }

  const clampRadius = (n: number) =>
    Math.max(LIGHT_MIN_RADIUS, Math.min(LIGHT_MAX_RADIUS, Math.round(n)))

  // Bright is the inner full-light radius; dim the outer extent (>= bright).
  // Keep legacy `radius` mirrored to bright for any v1 reader.
  const setBright = (next: number) => {
    if (!selectedLight) return
    const b = clampRadius(next)
    const dim = Math.max(selectedLight.dimRadius ?? b, b)
    updateLight({ brightRadius: b, radius: b, dimRadius: dim })
  }
  const setDim = (next: number) => {
    if (!selectedLight) return
    const bright = selectedLight.brightRadius ?? selectedLight.radius
    const d = Math.max(clampRadius(next), bright)
    updateLight({ dimRadius: d })
  }

  const handleLightDeleteClick = () => {
    if (!selectedLight) return
    removeLightSource(selectedLight.id)
    setSelectedLightId(null)
    try {
      socketService.deleteLightSource(selectedLight.id)
    } catch {
      // socket down — broadcast on reconnect reconciles
    }
  }

  // Reset transient action state whenever the selected token changes so
  // a primed delete / open editor doesn't leak across selections.
  const selectedTokenId = selectedToken?.id ?? null
  useEffect(() => {
    setIsEditing(false)
    setDeleteArmed(false)
    setHpEditing(false)
    setEditedName(selectedToken?.name ?? '')
    // Intentionally key only on the token id; we re-seed the editor name
    // on selection change, not on every external name edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTokenId])

  const handleSaveName = () => {
    if (
      selectedToken &&
      onRename &&
      editedName.trim() &&
      editedName.trim() !== selectedToken.name
    ) {
      onRename(selectedToken.id, editedName.trim())
    }
    setIsEditing(false)
  }

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName()
    } else if (e.key === 'Escape') {
      setEditedName(selectedToken?.name ?? '')
      setIsEditing(false)
    }
  }

  const handleDeleteClick = () => {
    if (!selectedToken || !onDelete) return
    if (!deleteArmed) {
      setDeleteArmed(true)
      window.setTimeout(() => setDeleteArmed(false), 4000)
      return
    }
    onDelete(selectedToken.id)
  }

  return (
    <div className="canvas-overlay">
      {/* Top-right: map title */}
      {map && (
        <div className="ov-tr">
          <div
            className="ac-hud-card"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.375rem 0.75rem',
              whiteSpace: 'nowrap',
            }}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: 'hsl(var(--ac-mint-primary))' }}
              aria-hidden
            >
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>
              {map.name}
            </span>
            <span
              style={{
                color: 'hsl(var(--ac-text-muted))',
                fontSize: '0.6875rem',
              }}
            >
              · {map.width}×{map.height}
            </span>
          </div>
        </div>
      )}

      {/* Bottom-left: selected-token HUD */}
      {showSelectedPanel && selectedToken && (
        <div className="ov-bl" style={{ width: 320 }}>
          <div className="ac-hud-card" style={{ padding: '0.75rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: selectedToken.color,
                  color: 'hsl(0 0% 10%)',
                  fontFamily: 'var(--ac-font-serif)',
                  fontSize: 22,
                  fontWeight: 500,
                  flexShrink: 0,
                }}
              >
                {selectedToken.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {isEditing ? (
                  <Input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={handleSaveName}
                    onKeyDown={handleRenameKeyDown}
                    autoFocus
                    maxLength={30}
                    style={{
                      height: 28,
                      fontSize: '0.875rem',
                      padding: '0 0.5rem',
                    }}
                  />
                ) : (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
                  >
                    <span
                      className="font-serif-ac"
                      style={{
                        fontSize: '1rem',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {selectedToken.name}
                    </span>
                    {canEdit && onRename && (
                      <IconButton
                        size="sm"
                        variant="ghost"
                        className="!w-[22px] !h-[22px]"
                        title="Edit name"
                        aria-label="Edit name"
                        onClick={() => {
                          setEditedName(selectedToken.name)
                          setIsEditing(true)
                        }}
                      >
                        <svg
                          width={11}
                          height={11}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                        </svg>
                      </IconButton>
                    )}
                  </div>
                )}
                <div
                  style={{
                    fontSize: '0.6875rem',
                    marginTop: 4,
                    color: 'hsl(var(--ac-text-muted))',
                  }}
                >
                  Pos {selectedToken.x},{selectedToken.y} · {Math.round(selectedToken.width * 10) / 10}×{Math.round(selectedToken.height * 10) / 10}
                  {selectedToken.visionRadius != null && (
                    <span> · Vision {selectedToken.visionRadius}sq</span>
                  )}
                </div>
              </div>
            </div>

            {/* Light/torch — owner-or-GM toggles this token's torch and sets
                its radius (grid squares). token.torchActive / lightRadius. */}
            {canEdit && onLightChange && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  marginTop: '0.5rem',
                }}
              >
                <button
                  type="button"
                  aria-pressed={!!selectedToken.torchActive}
                  onClick={() =>
                    onLightChange(selectedToken.id, {
                      torchActive: !selectedToken.torchActive,
                    })
                  }
                  style={{
                    flex: 1,
                    padding: '0.25rem 0.5rem',
                    borderRadius: 4,
                    border: '1px solid hsl(var(--ac-dark-accent))',
                    background: selectedToken.torchActive
                      ? 'hsl(var(--ac-gm-gold) / 0.25)'
                      : 'none',
                    color: 'hsl(var(--ac-text-light))',
                    fontSize: '0.6875rem',
                    cursor: 'pointer',
                  }}
                >
                  Torch {selectedToken.torchActive ? 'On' : 'Off'}
                </button>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.6875rem',
                    color: 'hsl(var(--ac-text-muted))',
                  }}
                >
                  Radius
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={selectedToken.lightRadius ?? 0}
                    onChange={(e) =>
                      onLightChange(selectedToken.id, {
                        lightRadius: Math.max(0, Number(e.target.value) || 0),
                      })
                    }
                    style={{
                      width: 44,
                      fontSize: '0.6875rem',
                      textAlign: 'center',
                      borderRadius: 4,
                      border: '1px solid hsl(var(--ac-dark-accent))',
                      background: 'hsl(var(--ac-dark-bg))',
                      color: 'hsl(var(--ac-text-light))',
                      padding: '0.125rem 0.25rem',
                    }}
                  />
                </label>
              </div>
            )}

            {/* Token actions — open the linked character sheet (in-app or
                new tab) and, for the GM, toggle the line-of-sight preview
                from this token. Sheet buttons need a linked character;
                LoS-view is GM-only and needs the token to actually have
                vision. Hidden entirely when none apply. */}
            {(selectedToken.characterId ||
              (isGM &&
                typeof selectedToken.visionRadius === 'number' &&
                selectedToken.visionRadius > 0)) && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  marginTop: '0.75rem',
                }}
              >
                {selectedToken.characterId && (
                  <button
                    type="button"
                    className="ac-icon-btn"
                    style={{ width: 28, height: 28 }}
                    title="Open character sheet"
                    aria-label="Open character sheet"
                    onClick={() =>
                      openCharacterSheet(selectedToken.characterId as string)
                    }
                  >
                    {/* open-book / sheet glyph */}
                    <svg
                      width={13}
                      height={13}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                  </button>
                )}
                {selectedToken.characterId && (
                  <a
                    href={getCharacterSheetUrl(selectedToken.characterId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ac-icon-btn"
                    style={{ width: 28, height: 28 }}
                    title="Open character sheet in a new tab"
                    aria-label="Open character sheet in a new tab"
                  >
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                )}
                {/* See as this token — GM only. Toggles viewing the board
                    from this token's perspective: fog + dynamic line-of-sight
                    rendered exactly as that token would see them. Active state
                    inverts to a teal fill. */}
                {isGM &&
                  typeof selectedToken.visionRadius === 'number' &&
                  selectedToken.visionRadius > 0 && (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      className="!w-[28px] !h-[28px]"
                      style={{
                        color:
                          previewingVisionFor === selectedToken.id
                            ? 'hsl(var(--ac-text-light))'
                            : undefined,
                        background:
                          previewingVisionFor === selectedToken.id
                            ? 'hsl(186 80% 40% / 0.7)'
                            : undefined,
                      }}
                      title={
                        previewingVisionFor === selectedToken.id
                          ? `Stop seeing as ${selectedToken.name}`
                          : `See as ${selectedToken.name}`
                      }
                      aria-label={
                        previewingVisionFor === selectedToken.id
                          ? 'Stop seeing as this token'
                          : 'See as this token'
                      }
                      aria-pressed={previewingVisionFor === selectedToken.id}
                      onClick={() =>
                        setPreviewingVisionFor(
                          previewingVisionFor === selectedToken.id
                            ? null
                            : selectedToken.id,
                        )
                      }
                    >
                      {/* eye glyph */}
                      <svg
                        width={13}
                        height={13}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </IconButton>
                  )}
              </div>
            )}

            {/* HP bar — only if character is linked AND uses the
                d20 stat layout. Non-d20 systems (burning-wheel,
                generic) skip the bar and use customStats below. */}
            {characterData && (characterData.systemKind ?? 'd20') === 'd20' && (
              <div style={{ marginTop: '0.75rem' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    fontSize: '0.625rem',
                  }}
                >
                  <span
                    style={{
                      color: 'hsl(var(--ac-text-muted))',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}
                  >
                    HP
                  </span>
                  <span className="font-mono-ac">
                    {hpEditing && canEdit && onHpEdit ? (
                      <input
                        autoFocus
                        value={hpInput}
                        onChange={(e) => setHpInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitHp()
                          else if (e.key === 'Escape') setHpEditing(false)
                        }}
                        onBlur={submitHp}
                        placeholder="N, +N, -N"
                        style={{
                          width: 60,
                          fontSize: '0.8125rem',
                          textAlign: 'center',
                          background: 'hsl(var(--ac-dark-accent))',
                          color: 'hsl(var(--ac-text-light))',
                          border: '1px solid hsl(var(--ac-mint-primary) / 0.5)',
                          borderRadius: 4,
                          padding: '0 0.25rem',
                        }}
                      />
                    ) : (
                      <b
                        style={{
                          color: 'hsl(var(--ac-text-light))',
                          fontSize: '0.8125rem',
                          cursor: canEdit && onHpEdit ? 'pointer' : 'default',
                        }}
                        title={canEdit && onHpEdit ? 'Click to edit HP (N to set, +N / -N to adjust)' : undefined}
                        onClick={() => {
                          if (!canEdit || !onHpEdit) return
                          setHpInput('')
                          setHpEditing(true)
                        }}
                      >
                        {displayHp.current}
                      </b>
                    )}
                    {displayHp.temp > 0 && (
                      <span style={{ color: 'hsl(var(--ac-info))' }}>
                        {' '}+{displayHp.temp}
                      </span>
                    )}
                    <span style={{ color: 'hsl(var(--ac-text-muted))' }}>
                      {' '}/ {displayHp.max}
                    </span>
                  </span>
                </div>
                <div className="vtt-hp-bar" style={{ marginTop: 4 }}>
                  <div
                    className="fill"
                    style={{
                      width: `${Math.max(0, Math.min(100, (displayHp.current / Math.max(1, displayHp.max)) * 100))}%`,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Stat grid.
                  - d20: AC / Speed / Init from character.stats
                  - non-d20 + character linked: first 3 customStats
                  - no character: token Size / Vision / Pos */}
            <div className="vtt-stat-grid" style={{ marginTop: '0.75rem' }}>
              {characterData && (characterData.systemKind ?? 'd20') === 'd20' ? (
                <>
                  <div className="vtt-stat">
                    <div className="k">AC</div>
                    <div className="v">{characterData.stats.ac}</div>
                  </div>
                  <div className="vtt-stat">
                    <div className="k">Speed</div>
                    <div className="v">
                      {characterData.stats.speed}
                      <small>ft</small>
                    </div>
                  </div>
                  <div className="vtt-stat">
                    <div className="k">Init</div>
                    <div className="v">
                      {characterData.stats.initiative >= 0 ? '+' : ''}
                      {characterData.stats.initiative}
                    </div>
                  </div>
                </>
              ) : characterData && characterData.customStats?.length ? (
                <>
                  {characterData.customStats.slice(0, 3).map((stat) => (
                    <div key={stat.key} className="vtt-stat">
                      <div className="k">{stat.label}</div>
                      <div className="v">{stat.value}</div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="vtt-stat">
                    <div className="k">Size</div>
                    <div className="v">
                      {Math.round(selectedToken.width * 10) / 10}×{Math.round(selectedToken.height * 10) / 10}
                    </div>
                  </div>
                  <div className="vtt-stat">
                    <div className="k">Vision</div>
                    <div className="v">
                      {selectedToken.visionRadius ?? 0}
                      <small>sq</small>
                    </div>
                  </div>
                  <div className="vtt-stat">
                    <div className="k">Pos</div>
                    <div className="v font-mono-ac" style={{ fontSize: '0.875rem' }}>
                      {selectedToken.x},{selectedToken.y}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Owner-or-GM: toggle whether other players can see this token's
                HP bar. The GM and the token's owner always see it; this only
                affects other players. */}
            {canEdit && onHpBarVisibilityChange && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginTop: '0.75rem',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid hsl(var(--ac-dark-accent))',
                  fontSize: '0.6875rem',
                  color: 'hsl(var(--ac-text-muted))',
                  cursor: 'pointer',
                }}
                title="When off, players cannot see this token's HP bar (you always can)."
              >
                <span>Players see HP bar</span>
                <input
                  type="checkbox"
                  checked={selectedToken.hpBarVisibleToPlayers !== false}
                  onChange={(e) =>
                    onHpBarVisibilityChange(selectedToken.id, e.target.checked)
                  }
                />
              </label>
            )}

            {/* VTT #526: owner-only private view toggle — hide THIS token's HP
                bar from the owner's own screen only. Independent of the
                "Players see HP bar" control above and never leaves the client.
                Shown to the owning player; a GM sees all bars by role. */}
            {canEdit && !isGM && (
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                  fontSize: '0.6875rem',
                  color: 'hsl(var(--ac-text-muted))',
                  cursor: 'pointer',
                }}
                title="Hide this token's HP bar on your own screen only. Doesn't affect what anyone else sees."
              >
                <span>Hide HP bar in my view</span>
                <input
                  type="checkbox"
                  checked={ownHpBarHidden.has(selectedToken.id)}
                  onChange={() => toggleOwnHpBarHidden(selectedToken.id)}
                />
              </label>
            )}

            {/* Destructive: two-step delete. First click arms, second
                confirms within 4s. GM-only. */}
            {canDelete && onDelete && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '0.75rem',
                  paddingTop: '0.5rem',
                  borderTop: '1px solid hsl(var(--ac-dark-accent))',
                }}
              >
                <IconButton
                  size="sm"
                  variant="destructive"
                  className="!w-[28px] !h-[28px]"
                  style={{
                    color: deleteArmed ? 'hsl(var(--ac-text-light))' : undefined,
                    background: deleteArmed ? 'hsl(var(--ac-danger))' : undefined,
                  }}
                  title={
                    deleteArmed
                      ? `Click again to delete ${selectedToken.name}`
                      : `Delete ${selectedToken.name}`
                  }
                  aria-label={
                    deleteArmed ? 'Confirm delete token' : 'Delete token'
                  }
                  onClick={handleDeleteClick}
                >
                  <svg
                    width={12}
                    height={12}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </IconButton>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom-left: selected-light HUD (GM). Lights reuse the token card
          chrome on desktop — ☀ identity, radius stepper, position, delete.
          Only when no token is selected (token takes the slot if both). */}
      {showSelectedPanel && isGM && selectedLight && !selectedToken && (
        <div className="ov-bl" style={{ width: 320 }}>
          <div className="ac-hud-card" style={{ padding: '0.75rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'hsl(var(--ac-gm-gold) / 0.25)',
                  color: 'hsl(var(--ac-gm-gold))',
                  fontSize: 24,
                  flexShrink: 0,
                }}
                aria-hidden
              >
                ☀
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span
                  className="font-serif-ac"
                  style={{ fontSize: '1rem', lineHeight: 1 }}
                >
                  Light
                </span>
                <div
                  style={{
                    fontSize: '0.6875rem',
                    marginTop: 4,
                    color: 'hsl(var(--ac-text-muted))',
                  }}
                >
                  Pos {selectedLight.x},{selectedLight.y}
                </div>
              </div>
            </div>

            {/* Bright + dim radius steppers */}
            {(
              [
                {
                  label: 'Bright',
                  value: selectedLight.brightRadius ?? selectedLight.radius,
                  set: setBright,
                },
                {
                  label: 'Dim',
                  value: selectedLight.dimRadius ?? selectedLight.radius,
                  set: setDim,
                },
              ] as const
            ).map((row) => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginTop: '0.5rem',
                }}
              >
                <span
                  style={{
                    fontSize: '0.625rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'hsl(var(--ac-text-muted))',
                    flex: 1,
                  }}
                >
                  {row.label}
                </span>
                <IconButton
                  size="sm"
                  variant="ghost"
                  className="!w-[28px] !h-[28px]"
                  title={`Decrease ${row.label.toLowerCase()} radius`}
                  aria-label={`Decrease ${row.label.toLowerCase()} radius`}
                  onClick={() => row.set(row.value - 1)}
                >
                  −
                </IconButton>
                <span
                  className="font-mono-ac"
                  style={{
                    minWidth: 32,
                    textAlign: 'center',
                    fontSize: '0.875rem',
                    color: 'hsl(var(--ac-text-light))',
                  }}
                >
                  {row.value}
                </span>
                <IconButton
                  size="sm"
                  variant="ghost"
                  className="!w-[28px] !h-[28px]"
                  title={`Increase ${row.label.toLowerCase()} radius`}
                  aria-label={`Increase ${row.label.toLowerCase()} radius`}
                  onClick={() => row.set(row.value + 1)}
                >
                  +
                </IconButton>
              </div>
            ))}

            {/* Colour tint */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.5rem',
                fontSize: '0.625rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'hsl(var(--ac-text-muted))',
              }}
            >
              <span style={{ flex: 1 }}>Color</span>
              <input
                type="color"
                value={selectedLight.color ?? '#ffffff'}
                onChange={(e) => updateLight({ color: e.target.value })}
                style={{
                  width: 32,
                  height: 24,
                  padding: 0,
                  border: '1px solid hsl(var(--ac-dark-accent))',
                  borderRadius: 4,
                  background: 'none',
                  cursor: 'pointer',
                }}
                aria-label="Light color"
              />
            </label>

            {/* Falloff % — how far the colour tint reaches into the dim band */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginTop: '0.5rem',
                fontSize: '0.625rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'hsl(var(--ac-text-muted))',
              }}
            >
              <span style={{ flex: 1 }}>Falloff</span>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={selectedLight.falloff ?? 0}
                onChange={(e) =>
                  updateLight({ falloff: Number(e.target.value) || 0 })
                }
                style={{ flex: 2 }}
                aria-label="Light falloff percent"
              />
              <span
                className="font-mono-ac"
                style={{
                  minWidth: 32,
                  textAlign: 'right',
                  color: 'hsl(var(--ac-text-light))',
                }}
              >
                {selectedLight.falloff ?? 0}%
              </span>
            </label>

            {/* Destructive: two-step delete. */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: '0.75rem',
                paddingTop: '0.5rem',
                borderTop: '1px solid hsl(var(--ac-dark-accent))',
              }}
            >
              <IconButton
                size="sm"
                variant="destructive"
                className="!w-[28px] !h-[28px]"
                title="Delete light"
                aria-label="Delete light"
                onClick={handleLightDeleteClick}
              >
                <svg
                  width={12}
                  height={12}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M3 6h18" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </IconButton>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
